
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
	Package,
	History,
	UserPen,
	ArrowLeftRight,
	Box,
	Hash,
	Clock
} from 'lucide-react'
import { formatUnixTimestamp } from '@/utils/format'

import { StatsCard } from '@/components/shared/StatsCard'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'
import { usePackageDetails, usePackageTransactionBlocks } from '@/hooks/useApi'

const PAGE_SIZE = 10;

interface TransactionBlocksProps { id: string };
function TransactionBlocks(props: TransactionBlocksProps) {
	const { id } = props;
	if (!id) {
		return <> Not Found </>;
	};

	const initialLoadCompleted = useRef<boolean>(false);

	const startingCursor = useRef<string | null>(null);
	const startingPageData = useRef<any>(null);

	const [cursor, setCursor] = useState<string | null>(null);

	const [pageToRender, setPageToRender] = useState<any>({});
	const [prevCursorHistory, setPrevCursorHistory] = useState<Array<string>>([]);
	const nextCursor = useRef<string | null | undefined>(null);

	const { data: packageTransactionBlocks } = usePackageTransactionBlocks(id, PAGE_SIZE, cursor);

	useEffect(() => {
		if (!initialLoadCompleted.current && packageTransactionBlocks?.hasNextPage) {
			startingCursor.current = packageTransactionBlocks.cursor;
			startingPageData.current = packageTransactionBlocks;
			initialLoadCompleted.current = true;
		}

		if (packageTransactionBlocks?.hasNextPage) {
			nextCursor.current = packageTransactionBlocks.cursor;
		}

		if (startingCursor.current === nextCursor.current) {
			setPageToRender(startingPageData.current);
		} else {
			setPageToRender(packageTransactionBlocks);
		}
	}, [packageTransactionBlocks])

	if (!packageTransactionBlocks) {
		return <>Loading...</>
	}

	return (
		<>
			<Pagination className='mb-2'>
				<PaginationContent>
					{/* Previous Button */}
					<PaginationItem>
						<PaginationPrevious className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Prev</span> as any}
							onClick={() => {
								if (prevCursorHistory.length === 1) {
									setCursor(null);
								}

								setPrevCursorHistory((prev: any) => {
									const temp = [...prev];
									temp.pop();
									if (temp.length) setCursor(temp[temp.length - 1]);
									return temp;
								})
							}}
						/>
					</PaginationItem>

					{/* Next Button */}
					<PaginationItem>
						<PaginationNext className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'
							content={<span className='text-xs'>Next</span> as any}
							onClick={() => {
								if (!nextCursor.current) return;
								setCursor(nextCursor.current);
								setPrevCursorHistory((prev: any) => [...prev, nextCursor.current]);
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>
			<div className="rounded-lg border">
				<Table>
					<TableHeader className='bg-[#C0E6FF]/10 shadow-lg'>
						<TableRow className='pointer-events-none'>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Checkpoint</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Digest</div>
								</div>
							</TableHead>
							<TableHead className='text-white'>
								<div className='flex flex-row items-center'>
									<div className='flex-1 text-xs font-medium'>Timestamp</div>
								</div>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{pageToRender?.data?.map((transactionBlock: any) => (
							<TableRow key={transactionBlock.digest} className='hover:bg-[#C0E6FF]/10 hover:backdrop-blur-md'>
								<TableCell className='text-xs'>{Number(transactionBlock.checkpoint).toLocaleString()}</TableCell>
								<TableCell className='text-xs'>
									<Link to={`/transaction-blocks/${transactionBlock.digest}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
										<span className='hidden md:block'>{`${transactionBlock.digest.slice(0, 6)}...${transactionBlock.digest.slice(-6)}`}</span>
										<span className='md:hidden'>{`${transactionBlock.digest.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell>{formatUnixTimestamp(Number(transactionBlock.timestampMS))}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	)
}


export const PackageDetailsPage = () => {
	const { id } = useParams<{ id: string }>()
	if (!id) {
		return <>Not Found</>;
	}

	const { data: packageDetails } = usePackageDetails(id);

	if (!packageDetails) {
		return <>Loading...</>
	}

	return (
		<div className="flex flex-col overflow-hidden w-full h-full">
			<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
			>
				<div className="flex flex-col w-full h-full gap-2 px-2 lg:px-4 pt-4">
					<h1 className="scroll-m-20 text-md lg:text-2xl font-bold tracking-tight">
						Package Details
					</h1>

					<div className='grid grid-cols-1'>
						<StatsCard
							title="Package ID"
							className='text-sm'
							valueStyleOverride='text-lg lg:text-xl overflow-hidden whitespace-nowrap text-ellipsis'
							value={packageDetails?.id}
							icon={<Package className='h-4 w-4'/>}
						/>
					</div>
					<div className='grid grid-cols-1 md:grid-cols-5 gap-2'>
						<div className='md:col-span-4'>
							<StatsCard
								title="Publisher"
								className='text-sm'
								valueStyleOverride='text-lg lg:text-xl overflow-hidden whitespace-nowrap text-ellipsis'
								value={
									<Link 
										to={`/accounts/${packageDetails.publisher}`} className="flex items-center space-x-2 text-[#4DA2FF] hover:text-[#C0E6FF]">
										{packageDetails.publisher}
									</Link>
								}
								icon={<UserPen className='h-4 w-4'/>}
							/>
						</div>
						<div className='md:col-span-1'>
							<StatsCard
								title="Version"
								className='text-sm'
								valueStyleOverride='text-md xl:text-xl'
								value={packageDetails?.version}
								icon={<History className='h-4 w-4'/>}
							/>
						</div>
					</div>


					<div className='flex flex-row gap-6'>


					</div>
					<Card className='bg-transparent'>
						<CardHeader className="flex flex-col justify-start space-y-0 p-0 min-w-60">
							<div className='flex flex-row items-center px-2 pt-2'>
								<div className="rounded-full px-2 transition-colors">
									<ArrowLeftRight className='h-5 w-5' />
								</div>
								<div className='flex-1'>
									<CardTitle className="text-sm md:text-md lg:text-lg font-medium">Transaction Blocks</CardTitle>
								</div>
							</div>
							<div className='px-4'>
								<CardDescription className='text-xs lg:text-md'>
									Transaction activity for this package
								</CardDescription>
							</div>
						</CardHeader>
						<CardContent className='px-2'>
							<TransactionBlocks id={id} />
						</CardContent>
					</Card>


				</div>
			</div>
		</div >
	)
}